/**
 * The API tab's pure parts (components/api): the request a selected endpoint starts, path parameters, an example
 * body from a JSON schema and the validation hint, "Copy as curl" with secrets masked, the History kept in this
 * browser (per project, the last 50, secrets masked), the short result Compare roles shows, sizes and status names.
 */

export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
export const READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];
export const MASK = '••••••';
const HISTORY_MAX = 50;
const HISTORY_BODY_MAX = 20000;
const SECRET_HEADER = /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key|api-key|x-auth-token|x-access-token|x-csrf-token)$|token|secret|password|apikey|api-key|session/i;

export const isReadMethod = (method) => READ_METHODS.includes(String(method || '').toUpperCase());
export const isSecretHeader = (name) => SECRET_HEADER.test(String(name || ''));
export const methodClass = (method) => `is-${String(method || 'get').toLowerCase()}`;

/** A header value masked as the server masks it: `Bearer ••••••`, `app_session=••••••`, else `••••••`. */
export function maskValue(name, value) {
  const text = String(value ?? '');
  if (/^cookie$/i.test(name)) { return text.split(';').map((pair) => (pair.includes('=') ? `${pair.split('=')[0].trim()}=${MASK}` : pair)).join('; '); }
  const scheme = /^(Bearer|Basic|Token|Digest)\s+/i.exec(text);
  return scheme ? `${scheme[1]} ${MASK}` : MASK;
}

export const maskHeaders = (pairs) => (pairs || []).map(([name, value]) => [name, isSecretHeader(name) ? maskValue(name, value) : value]);

/** The names of a path's parameters (`/messages/{id}` → ['id']). */
export const pathParamNames = (path) => [...String(path || '').matchAll(/\{([A-Za-z_]\w*)\}/g)].map((m) => m[1]);

/** The path with its parameters filled (URL-encoded); the ones left empty stay `{name}`. */
export function fillPath(path, values = {}) {
  return String(path || '').replace(/\{([A-Za-z_]\w*)\}/g, (whole, name) => {
    const value = values[name];
    return value === undefined || value === null || value === '' ? whole : encodeURIComponent(String(value));
  });
}

/** An example value for a JSON schema: example, default, the first enum, else one by type (objects a few levels). */
export function exampleFor(schema, depth = 0) {
  if (!schema || typeof schema !== 'object' || depth > 5) { return null; }
  if (schema.example !== undefined) { return schema.example; }
  if (schema.default !== undefined) { return schema.default; }
  if (Array.isArray(schema.enum) && schema.enum.length) { return schema.enum[0]; }
  const variants = schema.oneOf || schema.anyOf;
  if (Array.isArray(variants) && variants.length) { return exampleFor(variants[0], depth + 1); }
  if (Array.isArray(schema.allOf)) { return schema.allOf.reduce((out, part) => ({ ...out, ...(exampleFor(part, depth + 1) || {}) }), {}); }
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type || (schema.properties ? 'object' : undefined);
  if (type === 'object') {
    return Object.fromEntries(Object.entries(schema.properties || {}).map(([key, value]) => [key, exampleFor(value, depth + 1)]));
  }
  if (type === 'array') { return schema.items ? [exampleFor(schema.items, depth + 1)] : []; }
  if (type === 'integer' || type === 'number') { return typeof schema.minimum === 'number' ? schema.minimum : 0; }
  if (type === 'boolean') { return false; }
  if (type === 'string') {
    if (schema.format === 'email') { return 'ada@example.com'; }
    if (schema.format === 'date-time') { return new Date(0).toISOString(); }
    if (schema.format === 'date') { return '1970-01-01'; }
    if (schema.format === 'uuid') { return '00000000-0000-0000-0000-000000000000'; }
    return typeof schema.minLength === 'number' && schema.minLength > 6 ? 'x'.repeat(schema.minLength) : 'string';
  }
  return null;
}

const typeOfValue = (value) => {
  if (value === null) { return 'null'; }
  if (Array.isArray(value)) { return 'array'; }
  if (Number.isInteger(value)) { return 'integer'; }
  return typeof value;
};

const typeMatches = (want, value) => {
  const got = typeOfValue(value);
  const wants = Array.isArray(want) ? want : [want];
  return wants.some((w) => w === got || (w === 'number' && got === 'integer') || (w === 'null' && value === null));
};

/** The schema problems of `value`, a few, by path (`message: required`, `priority: one of low, normal`). */
export function schemaProblems(schema, value, at = '', depth = 0) {
  if (!schema || typeof schema !== 'object' || depth > 5) { return []; }
  const where = at || 'body';
  if (schema.type && !typeMatches(schema.type, value)) { return [`${where}: should be ${Array.isArray(schema.type) ? schema.type.join(' or ') : schema.type}`]; }
  if (Array.isArray(schema.enum) && !schema.enum.some((option) => JSON.stringify(option) === JSON.stringify(value))) {
    return [`${where}: one of ${schema.enum.map((option) => String(option)).join(', ')}`];
  }
  const problems = [];
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const key of schema.required || []) { if (value[key] === undefined) { problems.push(`${at ? `${at}.` : ''}${key}: required`); } }
    for (const [key, child] of Object.entries(schema.properties || {})) {
      if (value[key] !== undefined) { problems.push(...schemaProblems(child, value[key], at ? `${at}.${key}` : key, depth + 1)); }
    }
  }
  if (Array.isArray(value) && schema.items) { value.slice(0, 20).forEach((item, index) => problems.push(...schemaProblems(schema.items, item, `${where}[${index}]`, depth + 1))); }
  return problems.slice(0, 5);
}

/**
 * The hint under the JSON body: `{ tone: 'ok' | 'warn' | 'bad' | 'none', text }`. Empty: none; not JSON: bad, with
 * where; with a schema: whether it matches it.
 */
export function bodyHint(text, schema = null, schemaName = null) {
  if (!String(text || '').trim()) { return { tone: 'none', text: schema ? 'Empty: "Fill from schema" writes an example' : '' }; }
  let value;
  try {
    value = JSON.parse(text);
  } catch (error) {
    return { tone: 'bad', text: `Not valid JSON: ${String(error.message || error).replace(/^JSON\.parse: /, '')}` };
  }
  if (!schema) { return { tone: 'ok', text: 'Valid JSON' }; }
  const problems = schemaProblems(schema, value);
  if (!problems.length) { return { tone: 'ok', text: schemaName ? `Matches the schema ${schemaName}` : 'Matches the schema' }; }
  return { tone: 'warn', text: `Does not match${schemaName ? ` ${schemaName}` : ' the schema'}: ${problems.join('; ')}` };
}

/** The editable request an endpoint starts: `{ service, method, path, params, query, headers, body }`. */
export function requestFor(service, endpoint) {
  const params = {};
  for (const name of pathParamNames(endpoint.path)) { params[name] = ''; }
  const query = (endpoint.params || []).filter((p) => p.in === 'query').map((p) => ({ name: p.name, value: '', on: Boolean(p.required) }));
  const headers = (endpoint.params || []).filter((p) => p.in === 'header').map((p) => ({ name: p.name, value: '', on: Boolean(p.required) }));
  const body = endpoint.body && !isReadMethod(endpoint.method) ? JSON.stringify(exampleFor(endpoint.body.schema) ?? {}, null, 2) : '';
  return { service, method: endpoint.method, path: endpoint.path, params, query, headers, body, endpointKey: `${service} ${endpoint.method} ${endpoint.path}` };
}

const enabledRows = (rows) => (rows || []).filter((row) => row.on !== false && String(row.name || '').trim());

/** What `POST /api/send` gets for the request (without `as`). */
export function sendInput(request) {
  return {
    service: request.service,
    method: request.method,
    path: fillPath(request.path, request.params),
    query: enabledRows(request.query).map((row) => [row.name.trim(), row.value ?? '']),
    headers: enabledRows(request.headers).map((row) => [row.name.trim(), row.value ?? '']),
    ...(isReadMethod(request.method) || !String(request.body || '').trim() ? {} : { body: request.body }),
  };
}

const shellQuote = (value) => `'${String(value).replace(/'/g, "'\\''")}'`;

/**
 * "Copy as curl": the request against the service's preview URL (or `$PREVIEW_URL`), with what Flow added (the app
 * session for an app user, the person's token) masked, like every secret header.
 */
export function curlFor(request, { baseUrl = null, sent = null, as = null } = {}) {
  const input = sendInput(request);
  const base = String(baseUrl || '$PREVIEW_URL').replace(/\/+$/, '');
  const search = new URLSearchParams(input.query.map(([name, value]) => [name, value])).toString();
  const path = sent?.path || `${input.path}${search ? `${input.path.includes('?') ? '&' : '?'}${search}` : ''}`;
  let headers = sent?.headers || [...input.headers];
  if (!sent) {
    if (as?.kind === 'token' && as.header?.name) { headers.push([as.header.name, as.header.value || '']); }
    if (as?.kind === 'user') { headers.push(['Cookie', 'app_session=']); }
    if (input.body !== undefined && !headers.some(([name]) => /^content-type$/i.test(name))) { headers.push(['Content-Type', 'application/json']); }
  }
  headers = maskHeaders(headers);
  const parts = ['curl', ...(input.method === 'GET' ? [] : ['-X', input.method]), shellQuote(`${base}${path}`)];
  for (const [name, value] of headers) { parts.push('-H', shellQuote(`${name}: ${value}`)); }
  if (input.body !== undefined) { parts.push('--data-raw', shellQuote(input.body)); }
  return parts.join(' ');
}

// ---- History (this browser, per project) ----

const historyKey = (projectId) => `flow.api.history.${projectId}`;

export function readHistory(projectId) {
  try {
    const list = JSON.parse(localStorage.getItem(historyKey(projectId)) || '[]');
    return Array.isArray(list) ? list.slice(0, HISTORY_MAX) : [];
  } catch {
    return [];
  }
}

/** A call kept in History: secret header values masked, a token never kept, the body cut. */
export function historyEntry(request, { as, status = null, ms = null, error = null } = {}) {
  const mask = (rows) => (rows || []).map((row) => ({ ...row, value: isSecretHeader(row.name) ? maskValue(row.name, row.value) : row.value }));
  return {
    id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    at: new Date().toISOString(),
    service: request.service,
    method: request.method,
    path: request.path,
    params: { ...(request.params || {}) },
    query: request.query || [],
    headers: mask(request.headers),
    body: String(request.body || '').slice(0, HISTORY_BODY_MAX),
    as: as ? { kind: as.kind, ...(as.userId ? { userId: as.userId } : {}), ...(as.label ? { label: as.label } : {}), ...(as.kind === 'token' && as.header?.name ? { header: as.header.name } : {}) } : { kind: 'visitor' },
    status,
    ms,
    ...(error ? { error: String(error).slice(0, 200) } : {}),
  };
}

export function addToHistory(projectId, entry) {
  const list = [entry, ...readHistory(projectId)].slice(0, HISTORY_MAX);
  try { localStorage.setItem(historyKey(projectId), JSON.stringify(list)); } catch { /* not kept */ }
  return list;
}

export function clearHistory(projectId) {
  try { localStorage.removeItem(historyKey(projectId)); } catch { /* nothing kept */ }
  return [];
}

// ---- The token the person gives (this browser only) ----

const tokenKey = (projectId) => `flow.api.token.${projectId}`;
export function readToken(projectId) {
  try {
    const value = JSON.parse(localStorage.getItem(tokenKey(projectId)) || 'null');
    return value && typeof value.name === 'string' ? { name: value.name, value: String(value.value || '') } : { name: 'Authorization', value: '' };
  } catch {
    return { name: 'Authorization', value: '' };
  }
}
export function saveToken(projectId, token) {
  try { localStorage.setItem(tokenKey(projectId), JSON.stringify({ name: token.name, value: token.value })); } catch { /* not kept */ }
}

// ---- Showing an answer ----

const STATUS_NAMES = {
  200: 'OK', 201: 'Created', 202: 'Accepted', 204: 'No Content', 301: 'Moved Permanently', 302: 'Found', 304: 'Not Modified',
  400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found', 405: 'Method Not Allowed', 409: 'Conflict',
  413: 'Payload Too Large', 415: 'Unsupported Media Type', 422: 'Unprocessable Entity', 429: 'Too Many Requests',
  500: 'Internal Server Error', 502: 'Bad Gateway', 503: 'Service Unavailable', 504: 'Gateway Timeout',
};
export const statusName = (status, given = '') => given || STATUS_NAMES[status] || '';
export const statusTone = (status) => {
  if (status >= 200 && status < 300) { return 'ok'; }
  if (status >= 300 && status < 400) { return 'info'; }
  if (status >= 400 && status < 500) { return 'warn'; }
  return 'bad';
};

export function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) { return `${n} B`; }
  if (n < 1024 * 1024) { return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`; }
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** The answer's body for the Body tab: pretty JSON when it is JSON, else the text (binary: a note). */
export function prettyBody(answer) {
  if (!answer) { return { kind: 'none', text: '' }; }
  if (answer.bodyEncoding === 'base64') { return { kind: 'binary', text: `${formatSize(answer.size)} of ${answer.contentType || 'binary data'}` }; }
  const text = answer.body || '';
  if (/json/i.test(answer.contentType || '') || /^\s*[[{]/.test(text)) {
    try { return { kind: 'json', text: JSON.stringify(JSON.parse(text), null, 2) }; } catch { /* not JSON after all */ }
  }
  return { kind: 'text', text };
}

/** A few words on an answer for Compare roles: "12 orders", "3 items", `"Sign in first"`, else its start. */
export function shortResult(answer) {
  if (!answer) { return ''; }
  if (answer.bodyEncoding === 'base64') { return formatSize(answer.size); }
  let value;
  try { value = JSON.parse(answer.body || ''); } catch { return String(answer.body || '').replace(/\s+/g, ' ').trim().slice(0, 40); }
  if (Array.isArray(value)) { return `${value.length} item${value.length === 1 ? '' : 's'}`; }
  if (value && typeof value === 'object') {
    if (typeof value.error === 'string') { return `"${value.error.slice(0, 40)}"`; }
    if (typeof value.message === 'string' && answer.status >= 400) { return `"${value.message.slice(0, 40)}"`; }
    const list = Object.entries(value).find(([, child]) => Array.isArray(child));
    if (list) { return `${list[1].length} ${list[0]}`; }
    return JSON.stringify(value).slice(0, 40);
  }
  return String(value).slice(0, 40);
}

/** A user's label: "Ada (app user · admin)". */
export function userLabel(user) {
  const name = user.name || user.email;
  return `${name} (app user${user.roles?.length ? ` · ${user.roles.join(', ')}` : ''})`;
}

/** Endpoints matching the find box (method, path or summary). */
export function matchEndpoint(endpoint, find) {
  const q = String(find || '').trim().toLowerCase();
  if (!q) { return true; }
  return `${endpoint.method} ${endpoint.path} ${endpoint.summary || ''}`.toLowerCase().includes(q);
}
